import React, { useState } from "react";

import validate from "../utilities/validate";
import Alert from "./alert.jsx";
import Label from "./label.jsx";
/**
 * @typedef {import('../../types').showValidation} showValidation
 * @typedef {import('../../types').Component} Component
 */

/**
 * @typedef {object} Item - Items to be rendered
 * @property {string} label - The label for the checkbox
 * @property {string} responseId - The responseId for the checkbox
 */

/**
 * @typedef {object} SelectedItems
 * @property {Item[]} selectedItems - Whether the group is valid or not
 * @property {string} key - The key for the validation
 */

/**
 * @typedef {object} FormProps
 * @property {Component} component - The component to render
 * @property {showValidation} showValidation - Whether to show validation
 */

/**
 * @param {FormProps} props - props
 * @returns {React.JSX.Element} React function component
 */
function Checkboxes({ component, showValidation }) {
  const { responses, componentId, validation, feedback } = component;
  const [checkedItems, setCheckedItems] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);

  /**
   *
   * @param {React.SyntheticEvent<HTMLInputElement>} event - Synthetic event
   * @param {string} responseId - The responseId of the checkbox
   * @returns {void}
   */
  function onChange(event, responseId) {
    if (event.currentTarget.checked) {
      // @ts-ignore includes return type is incorrect
      const isAlreadySelected = checkedItems.includes(
        (item) => item.responseId,
      );

      if (!isAlreadySelected) {
        setCheckedItems([
          // @ts-ignore
          responseId,
          ...checkedItems,
        ]);
      }
    }
    if (!event.currentTarget.checked) {
      const nextCheckedItems = checkedItems.filter(
        (item) => item !== responseId,
      );
      // @ts-ignore
      setCheckedItems(nextCheckedItems);
    }
    // @ts-ignore - we always return validation
    const { isValid, key = "" } = validate(
      "checkboxes",
      // @ts-ignore - @TODO: Figure out how to correctly type React state with JSDoc
      checkedItems,
      validation,
    );
    // @ts-ignore - @TODO: Figure out how to correctly type React state with JSDoc
    showValidation(isValid, key, event.currentTarget.value);
    setShowFeedback(true);
  }
  return (
    <div className="fr-mb-4">
      {component.label && (
        <legend className="fr-mb-3 fr-text-lg">
          {component.label}
          {validation?.required && (
            <>
              <span className="fr-text-red-600">*</span>
              <span className="fr-sr-only">(This field is required)</span>
            </>
          )}
        </legend>
      )}
      {responses?.map(({ label, responseId }) => (
        <div
          key={`${componentId}-${responseId}`}
          className="fr-flex fr-h-5 fr-items-center fr-mb-2"
        >
          <input
            type="checkbox"
            value={label}
            id={`${componentId}-${responseId}`}
            name={`form-component-${componentId}`}
            onChange={(event) => onChange(event, responseId)}
            className="fr-w-4 fr-h-4 fr-rounded fr-border-zinc-300 hover:fr-ring-primary-500 focus-visible:fr-ring-primary-500 checked:fr-bg-primary-600 checked:hover:fr-bg-primary-600"
          />
          {label && (
            <div className="fr-pl-2">
              <Label value={label} htmlFor={`${componentId}-${responseId}`} />
            </div>
          )}
        </div>
      ))}
      {showFeedback && validation.required && checkedItems.length === 0 && (
        <>
          <Alert type="error" message={feedback.required} />
        </>
      )}
    </div>
  );
}

export default Checkboxes;
